import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deactivateUser,
  fetchUserLookup,
  fetchUsers,
  fetchUsersPaginated,
  reactivateUser,
  updateUserCapacity,
  updateUserContact,
  updateUserOrganization,
  updateUserRoles,
  type UserListFilters,
} from '../api/user.api';

// Root key — every query under 'users' (both flat lists, every paginated page/filter
// combination) is invalidated together on a mutation.
const USERS_ROOT_KEY = ['users'];
const USERS_KEY = [...USERS_ROOT_KEY, 'list'];
const USERS_LOOKUP_KEY = [...USERS_ROOT_KEY, 'lookup'];
const usersPaginatedKey = (filters: UserListFilters) => [...USERS_ROOT_KEY, 'paginated', filters];

// Full flat list (admin:read) — for admin-only screens only. See useUserLookup() below for
// every project-domain EntityPicker field.
export function useUserList() {
  return useQuery({ queryKey: USERS_KEY, queryFn: fetchUsers });
}

// Minimal active-user list (project:read) — the shared data source for every project-domain
// EntityPicker field (Owner/Assignee/Lead/Manager/Escalated-To). See fetchUserLookup()'s own
// comment for why this is a separate hook/endpoint from useUserList() above.
export function useUserLookup() {
  return useQuery({ queryKey: USERS_LOOKUP_KEY, queryFn: fetchUserLookup });
}

// Server-driven pagination — used by UsersPage.
export function useUserListPaginated(filters: UserListFilters) {
  return useQuery({
    queryKey: usersPaginatedKey(filters),
    queryFn: () => fetchUsersPaginated(filters),
    staleTime: 0,
  });
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roleIds }: { id: string; roleIds: string[] }) =>
      updateUserRoles(id, roleIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY }),
  });
}

export function useUpdateUserCapacity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, weeklyCapacityHours }: { id: string; weeklyCapacityHours: number }) =>
      updateUserCapacity(id, weeklyCapacityHours),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY }),
  });
}

export function useUpdateUserOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { isManagement: boolean; businessUnitId?: string | null } }) =>
      updateUserOrganization(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY }),
  });
}

export function useUpdateUserContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contact }: { id: string; contact: string | null }) => updateUserContact(id, contact),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY }),
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY }),
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reactivateUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ROOT_KEY }),
  });
}
